import React, { Component } from 'react';
import { connect } from 'react-redux';

import * as GroupInviteLinksSelectors from 'shared_redux/selectors/view/group_invite_links';
import {
    fetchGroupInviteLinks,
    createGroupInviteLink,
    updateGroupInviteLink,
    deleteGroupInviteLink,
    hideGroupInviteDialog
} from 'shared_redux/actions/group_invite_links';

import View from './view';

const mapStateToProps = (state, ownProps) => {
    // If forceShow is true, use that as the isShowing value
    const isShowing = ownProps.forceShow === true ? true : GroupInviteLinksSelectors.getIsShowingDialog(state);
    
    // Get the current group ID from different sources (in order of priority):
    // 1. Directly from component props
    // 2. From the redux store's currentGroupId
    // 3. From URL params via router state
    const groupId = ownProps.groupId || state.group_invite_links?.currentGroupId || null;
    
    // If we have router state, we might be able to get group info from URL
    let groupIdFromRouter = null;
    if (!groupId && state.router && state.router.location) {
        try {
            const searchParams = new URLSearchParams(state.router.location.search);
            groupIdFromRouter = searchParams.get('group_id');
        } catch (error) {
            console.error('Error parsing URL params:', error);
        }
    }
    
    const effectiveGroupId = groupId || groupIdFromRouter;
    
    // Get team and group name (try props first, then try to get from database)
    let teamId = ownProps.teamId || (state.active_filter && state.active_filter.teamId) || null;
    let teamName = ownProps.teamName || null;
    let groupName = ownProps.groupName || null;
    
    // If we don't have an effective group ID but we do have a team ID, try to get the default group
    if (!effectiveGroupId && teamId) {
        console.log(`[MapState] No group ID but have teamId ${teamId}, looking for default group`);
        // Check if we have team data in the database with default_group_id
        if (state.database?.Teams?.items?.[teamId]) {
            const team = state.database.Teams.items[teamId];
            // If the team has a default_group_id, use it
            if (team.default_group_id) {
                console.log(`[MapState] Found team default group ID: ${team.default_group_id}`);
                // Update the effective group ID with the team's default group
                effectiveGroupId = team.default_group_id;
            }
        }
    }
    
    // If we have a group ID but no group name, try to get it from the database
    if (effectiveGroupId && !groupName && state.database?.Groups?.items?.[effectiveGroupId]) {
        const group = state.database.Groups.items[effectiveGroupId];
        groupName = group.name;
        
        // If we don't have a team ID yet, get it from the group
        if (!teamId && group.team_id) {
            teamId = group.team_id;
            
            // And try to get the team name too
            if (state.database?.Teams?.items?.[teamId]) {
                teamName = state.database.Teams.items[teamId].name;
            }
        }
    }
    
    // Get all invite links from state
    const inviteLinks = GroupInviteLinksSelectors.getInviteLinks(state);
    
    // Find the active invite link for this specific group
    const groupInviteLink = effectiveGroupId ? 
        inviteLinks.find(link => link.group_id === effectiveGroupId && link.is_active === true) : 
        null;
    
    console.log('[MapState] Effective Group ID:', effectiveGroupId);
    console.log('[MapState] Found group invite link:', groupInviteLink);
    
    // Add detailed logging to debug team and group name issues
    console.log('[GroupInviteLink] Final state before render:', {
        teamId,
        teamName,
        teamNameType: typeof teamName,
        effectiveGroupId,
        groupName,
        groupNameType: typeof groupName,
        inviteLink: groupInviteLink
    });
    
    // Use team/group names from either:
    // 1. Redux state (from when the dialog was opened)
    // 2. Names retrieved from database lookups above
    // 3. Fallback to props if provided (rare)
    const finalTeamName = state.group_invite_links?.currentTeamName || teamName || null;
    const finalGroupName = state.group_invite_links?.currentGroupName || groupName || null;

    console.log('[GroupInviteLink] Final names for render:', {
        finalTeamName,
        finalGroupName,
        fromState: {
            teamName: state.group_invite_links?.currentTeamName || null,
            groupName: state.group_invite_links?.currentGroupName || null
        },
        fromLookup: { teamName, groupName }
    });

    return {
        isShowing,
        inviteLinks,
        inviteLink: groupInviteLink || null,
        isLoading: GroupInviteLinksSelectors.getIsLoading(state),
        teamId,
        teamName: finalTeamName,
        groupId: effectiveGroupId,
        groupName: finalGroupName,
        currentGroupId: state.group_invite_links?.currentGroupId || null
    };
};

const mapDispatchToProps = (dispatch) => ({
    onClose: () => dispatch(hideGroupInviteDialog()),
    saveInviteLink: (data) => dispatch(createGroupInviteLink(data)),
    updateInviteLink: (linkId, data) => dispatch(updateGroupInviteLink(linkId, data)),
    removeInviteLink: (inviteLink) => dispatch(deleteGroupInviteLink(inviteLink)),
    fetchInviteLinks: (groupId) => dispatch(fetchGroupInviteLinks(groupId))
});

class GroupInviteLink extends Component {
    constructor(props) {
        super(props);
        
        // Store a reference to the window's Redux state if it exists
        if (typeof window !== 'undefined') {
            window.__REDUX_STATE__ = window.__REDUX_STATE__ || window.__PRELOADED_STATE__;
        }
    }

    componentDidMount() {
        // Log all props to see what we're working with
        console.log('[Component] GroupInviteLink mounted with props:', {
            groupId: this.props.groupId,
            isShowing: this.props.isShowing
        });
        
        // Do the normal Redux fetch
        this.checkAndFetchLinks();
        
        // ALSO do a direct API call without Redux to ensure it happens
        this.directFetchLinks();
    }
    
    // Direct fetch using browser's fetch API - this ensures the backend actually gets a request
    directFetchLinks() {
        const groupId = this.getCurrentGroupId();
        if (!groupId) return;
        
        console.log(`[Component] Making direct API call to fetch invite links for group: ${groupId}`);
        
        // Direct API call to the backend
        fetch(`/api/v1/group-invite-links/groups/${groupId}`)
            .then(response => {
                console.log(`[Component] Direct API call status: ${response.status}`);
                return response.json();
            })
            .then(data => {
                console.log('[Component] Direct API call result:', data);
                
                // If we get a result, store it globally for the UI to use
                if (data) {
                    if (window.directInviteLinks === undefined) {
                        window.directInviteLinks = {};
                    }
                    
                    window.directInviteLinks[groupId] = data;
                    console.log(`[Component] Stored direct invite links for group: ${groupId}`);
                    
                    // Force the parent component to update if possible
                    if (window.updateInviteLinkState) {
                        window.updateInviteLinkState(groupId, true);
                    }
                }
            })
            .catch(error => {
                console.error('[Component] Direct API call error:', error);
            });
    }

    componentDidUpdate(prevProps) {
        // Log props changes when they happen
        if (prevProps.groupId !== this.props.groupId) {
            console.log('[Component] Group ID changed from', prevProps.groupId, 'to', this.props.groupId);
        }
        
        if (prevProps.isShowing !== this.props.isShowing) {
            console.log('[Component] Dialog visibility changed from', prevProps.isShowing, 'to', this.props.isShowing);
        }
        
        // Check and fetch on any relevant prop change
        if (prevProps.groupId !== this.props.groupId || prevProps.isShowing !== this.props.isShowing) {
            this.checkAndFetchLinks();
        }
    }
    
    // Get current valid group ID from various sources
    getCurrentGroupId() {
        // Always check for the latest group ID from URL first since this is most reliable for navigation
        try {
            if (typeof window !== 'undefined') {
                const params = new URLSearchParams(window.location.search);
                const groupIdFromUrl = params.get('group_id');
                if (groupIdFromUrl) {
                    console.log('[Component] Got group ID from URL:', groupIdFromUrl);
                    return groupIdFromUrl;
                }
            }
        } catch (error) {
            console.error('Error getting group ID from URL:', error);
        }
        
        // Then try the prop passed directly
        if (this.props.groupId) {
            console.log('[Component] Using group ID from props:', this.props.groupId);
            return this.props.groupId;
        }
        
        // Check for team's default group ID if we have a team ID
        if (this.props.teamId) {
            // Try to get the team from Redux store
            try {
                // Access the global store via window.__REDUX_STATE__ to inspect it
                // This is just for diagnostics
                if (typeof window !== 'undefined' && window.__REDUX_STATE__) {
                    console.log('[Component] Redux state available:', 
                      window.__REDUX_STATE__.database?.Teams?.items?.[this.props.teamId] ? 'Team found' : 'Team not found');
                }
                
                // Check if the database has been populated with our teams
                const state = window.__REDUX_STATE__ || {};
                const team = state.database?.Teams?.items?.[this.props.teamId];
                
                if (team && team.default_group_id) {
                    console.log(`[Component] Using team's default group ID: ${team.default_group_id}`);
                    return team.default_group_id;
                } else {
                    console.log(`[Component] Team found but no default_group_id:`, team);
                }
            } catch (error) {
                console.error('[Component] Error accessing team data:', error);
            }
        }
        
        // Finally check for a current group ID in Redux state
        if (this.props.currentGroupId) {
            console.log('[Component] Using group ID from Redux:', this.props.currentGroupId);
            return this.props.currentGroupId;
        }
        
        console.warn('[Component] No group ID found from any source');
        return null;
    }
    
    // Check if we have a valid group ID and fetch invite links if needed
    checkAndFetchLinks() {
        const groupId = this.getCurrentGroupId();
        
        // Only fetch when we have both a group ID and the dialog is showing
        if (groupId && this.props.isShowing) {
            console.log('[Component] Fetching invite links for group:', groupId);
            this.props.fetchInviteLinks(groupId);
        } else if (this.props.isShowing) {
            console.warn('[Component] Cannot fetch links - missing group ID');
        }
    }

    render() {
        // Allow showing via Redux state (isShowing) or via direct prop (forceShow)
        const shouldShow = this.props.isShowing || this.props.forceShow; 
        
        // Don't render anything if we shouldn't show
        if (!shouldShow) {
            return null;
        }

        // Create a safe version of props to avoid undefined errors
        const safeProps = {
            ...this.props,
            teamName: this.props.teamName || '',
            groupName: this.props.groupName || '',
            inviteLink: this.props.inviteLink || null,
            inviteLinks: Array.isArray(this.props.inviteLinks) ? this.props.inviteLinks : [],
            isLoading: !!this.props.isLoading,
            groupId: this.props.groupId || null
        };

        return <View {...safeProps} />;
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(GroupInviteLink);
